import type { ReactNode } from 'react';
import { DominoLogo } from '@/components/ui/DominoLogo';
import { cn } from '@/lib/utils';

export const SIGNUP_STEPS = ['Votre entreprise', 'Votre compte', 'L’analyste'] as const;

export const signupPrimary =
  'min-h-[52px] rounded-full bg-domino-aubergine px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-domino-prune disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-domino-prune';

/** Left column: what the diagnostic gives, over the 70s sun arcs. */
export const SignupAside = () => (
  <aside className="relative flex min-w-0 flex-[1_1_420px] flex-col gap-6 overflow-hidden bg-domino-aubergine px-6 py-8 text-white md:gap-9 md:px-16 md:pb-56 md:pt-12">
    {/* The arcs keep to the bottom band reserved by pb-56: the text never sits on them. On a phone, only the title. */}
    <div aria-hidden className="pointer-events-none absolute -bottom-[720px] hidden md:block -left-[260px] h-[900px] w-[900px]">
      <div className="absolute inset-0 rounded-full bg-domino-coral" />
      <div className="absolute inset-[90px] rounded-full bg-domino-sun" />
      <div className="absolute inset-[180px] rounded-full bg-domino-sunSoft" />
    </div>
    <a href="https://dominoflow.com" className="relative flex items-center gap-2.5 self-start font-display text-2xl font-semibold text-white">
      <DominoLogo tile className="h-9 w-9" /> Domino
    </a>
    <div className="relative flex max-w-[30em] flex-col gap-4">
      <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-domino-sun md:text-5xl">
        Votre diagnostic commercial, gratuit.
      </h1>
      <p className="hidden text-lg text-domino-plumTint md:block">
        L’analyste de Domino étudie votre produit, votre marché et votre façon de vendre, pour repérer
        où l’IA peut créer de la valeur tout de suite. Vous recevez :
      </p>
      <ul className="hidden flex-col gap-3.5 md:flex">
        <Benefit strong="Les goulots d’étranglement de votre vente" rest=" que l’IA peut lever, avec les constats qui le prouvent" />
        <Benefit strong="Des objectifs chiffrés" rest=" et les leviers pour les atteindre" />
        <Benefit strong="Les premiers outils à construire" rest=" pour votre équipe" />
      </ul>
    </div>
  </aside>
);

const Benefit = ({ strong, rest }: { strong: string; rest: string }) => (
  <li className="flex items-start gap-3">
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden className="mt-0.5 flex-none">
      <circle cx="11" cy="11" r="10" fill="#FFD84D" />
      <path d="M6.5 11.5l3 3 6-6.5" fill="none" stroke="#4A1F4E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span><strong>{strong}</strong>{rest}</span>
  </li>
);

export const SignupStepper = ({ step }: { step: number }) => (
  <ol aria-label="Étapes" className="flex flex-wrap gap-2">
    {SIGNUP_STEPS.map((label, index) => (
      <li
        key={label}
        aria-current={index === step ? 'step' : undefined}
        className={cn(
          'rounded-full px-3.5 py-2 text-sm font-semibold',
          index === step ? 'bg-domino-sun font-bold text-domino-ink' : index < step ? 'bg-domino-lilac text-domino-aubergine' : 'bg-domino-muted text-domino-inkSoft',
        )}
      >
        {index + 1}. {label}
      </li>
    ))}
  </ol>
);

export const SignupHeading = ({ title, children }: { title: string; children?: ReactNode }) => (
  <div className="flex flex-col gap-2">
    <h2 className="font-display text-4xl font-semibold leading-tight text-domino-aubergine">{title}</h2>
    {children && <p className="text-domino-inkSoft">{children}</p>}
  </div>
);

export const SignupField = (props: {
  id: string; label: string; value: string; onChange: (value: string) => void;
  type?: string; placeholder?: string; autoComplete?: string; hint?: string; required?: boolean;
}) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={props.id} className="text-sm font-semibold text-domino-ink">{props.label}</label>
    <input
      id={props.id}
      type={props.type ?? 'text'}
      value={props.value}
      required={props.required}
      onChange={(event) => props.onChange(event.target.value)}
      placeholder={props.placeholder}
      autoComplete={props.autoComplete}
      className="min-h-[52px] rounded-xl border border-domino-sand bg-domino-warm px-4 py-3.5 text-base text-domino-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-domino-prune"
    />
    {props.hint && <span className="text-[13px] text-domino-inkSoft">{props.hint}</span>}
  </div>
);

export const SignupChoices = <K extends string>(props: {
  legend: string; options: ReadonlyArray<{ key: K; label: string }>; value: K; onChange: (value: K) => void;
}) => (
  <fieldset className="flex flex-col gap-2.5">
    <legend className="mb-2.5 text-sm font-semibold text-domino-ink">{props.legend}</legend>
    <div className="flex flex-wrap gap-2">
      {props.options.map((option) => {
        const on = option.key === props.value;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={on}
            onClick={() => props.onChange(option.key)}
            className={cn(
              'min-h-[44px] rounded-full border px-4 py-2.5 text-[15px] font-semibold transition-colors',
              on ? 'border-domino-aubergine bg-domino-aubergine text-white' : 'border-domino-border bg-domino-warm text-domino-aubergine hover:bg-domino-lilac',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export const SignupAlert = ({ children }: { children: ReactNode }) => (
  <p role="alert" className="max-w-[560px] rounded-xl bg-red-50 px-4 py-3 text-[15px] text-red-800">{children}</p>
);
